import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Upload,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import BulkLinkButton from '@/components/BulkLinkButton';
import { ExternalLink } from '@/components/ui/external-link';
import MappingDetailPanel from '@/components/mappings/MappingDetailPanel';
import type { MappingKind } from '@/components/mappings/MappingDetailPanel';
import LinkMappingModal from '@/components/mappings/LinkMappingModal';
import type { StagedPair } from '@/components/mappings/LinkMappingModal';
import {
  hubspotProductUrl,
  stripePriceUrl,
  stripeMeterUrl,
} from '@/utils/externalUrls';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type {
  ProductMappingItem,
  ProductMappingRequest,
  ProductMappingDryRunResponse,
  ProductMappingValidationResult,
  LineItemOverrideItem,
  MeterMappingItem,
  MeterMappingRequest,
  MeterMappingDryRunResponse,
  MeterMappingValidationResult,
} from '@/types/api';

const ProductMappings: React.FC = () => {
  const queryClient = useQueryClient();

  // Product mapping dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [dryRunDialogOpen, setDryRunDialogOpen] = useState(false);
  const [dryRunResults, setDryRunResults] = useState<ProductMappingDryRunResponse | null>(null);
  const [pendingMappings, setPendingMappings] = useState<ProductMappingRequest[]>([]);

  // Meter mapping dialog state
  const [meterAddDialogOpen, setMeterAddDialogOpen] = useState(false);
  const [meterCsvDialogOpen, setMeterCsvDialogOpen] = useState(false);
  const [meterDryRunDialogOpen, setMeterDryRunDialogOpen] = useState(false);
  const [meterDryRunResults, setMeterDryRunResults] = useState<MeterMappingDryRunResponse | null>(null);
  const [pendingMeterMappings, setPendingMeterMappings] = useState<MeterMappingRequest[]>([]);

  // Expanded row state, keyed by "<table>:<row id>"
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // CSV file refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const meterFileInputRef = useRef<HTMLInputElement>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);
  // Non-fatal advisory from the negative-line-item toggle
  const [negativeNotice, setNegativeNotice] = useState<string | null>(null);

  // Tenant settings for external dashboard links
  const { data: tenantSettings } = useQuery({
    queryKey: ['tenantSettings'],
    queryFn: () => apiClient.getTenantSettings(),
  });
  const portalId = tenantSettings?.hubspot_portal_id;
  // No fallback: while settings load, IDs render as plain text rather than
  // linking a live-mode tenant to the test dashboard.
  const stripeEnv = tenantSettings?.stripe_environment;
  const stripeAcctId = tenantSettings?.stripe_account_id;

  // Fetch product mappings (Table 1)
  const { data: productData, isLoading: productLoading, isError: productError } = useQuery({
    queryKey: ['productMappings'],
    queryFn: () => apiClient.listProductMappings(),
  });

  // Fetch line item overrides (Table 2)
  const { data: overrideData, isLoading: overrideLoading, isError: overrideError } = useQuery({
    queryKey: ['lineItemOverrides'],
    queryFn: () => apiClient.listLineItemOverrides(),
  });

  // Fetch meter mappings (Table 3)
  const { data: meterData, isLoading: meterLoading, isError: meterError } = useQuery({
    queryKey: ['meterMappings'],
    queryFn: () => apiClient.listMeterMappings(),
  });

  // Validate mutation
  const validateMutation = useMutation({
    mutationFn: (mappings: ProductMappingRequest[]) =>
      apiClient.validateProductMappings(mappings),
    onSuccess: (results, variables) => {
      setDryRunResults(results);
      setPendingMappings(variables);
      setAddDialogOpen(false);
      setCsvDialogOpen(false);
      setDryRunDialogOpen(true);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Validation failed');
    },
  });

  // CSV validate mutation
  const csvValidateMutation = useMutation({
    mutationFn: (file: File) => apiClient.validateProductMappingsCsv(file),
    onSuccess: (results) => {
      const validMappings = results.results
        .filter((r) => r.hubspot_valid && r.stripe_valid)
        .map((r) => ({
          hubspot_product_id: r.hubspot_product_id,
          stripe_price_id: r.stripe_price_id,
          // Carried through from the CSV column so the save keeps it.
          line_item_is_negative: r.line_item_is_negative,
        }));
      setDryRunResults(results);
      setPendingMappings(validMappings);
      setCsvDialogOpen(false);
      setDryRunDialogOpen(true);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'CSV validation failed');
    },
  });

  // Negative-line-item toggle on an existing mapping
  const negativeMutation = useMutation({
    mutationFn: ({ hubspotProductId, isNegative }: { hubspotProductId: string; isNegative: boolean }) =>
      apiClient.setProductMappingNegative(hubspotProductId, isNegative),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['productMappings'] });
      // The update is saved either way; the advisory says the product will
      // fail to sync until it bills one-time, so it must not read as clean.
      setNegativeNotice(
        response.negative_warning
          ? `${response.hubspot_product_id}: ${response.negative_warning}`
          : null
      );
    },
    onError: (err: any) => {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          'Failed to update the negative line item setting'
      );
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (mappings: ProductMappingRequest[]) =>
      apiClient.saveProductMappings(mappings),
    onSuccess: (response) => {
      setDryRunDialogOpen(false);
      setDryRunResults(null);
      setPendingMappings([]);
      queryClient.invalidateQueries({ queryKey: ['productMappings'] });
      // The save endpoint returns 200 even when individual rows fail —
      // surface those instead of silently dropping them.
      setError(summarizeSaveFailures(response.results, 'mapping'));
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Save failed');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (hubspotProductId: string) =>
      apiClient.deleteProductMapping(hubspotProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productMappings'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Delete failed');
    },
  });

  // ── Meter Mapping Mutations ─────────────────────────────────────────

  const meterValidateMutation = useMutation({
    mutationFn: (mappings: MeterMappingRequest[]) =>
      apiClient.validateMeterMappings(mappings),
    onSuccess: (results, variables) => {
      setMeterDryRunResults(results);
      setPendingMeterMappings(variables);
      setMeterAddDialogOpen(false);
      setMeterCsvDialogOpen(false);
      setMeterDryRunDialogOpen(true);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Meter validation failed');
    },
  });

  const meterCsvValidateMutation = useMutation({
    mutationFn: (file: File) => apiClient.validateMeterMappingsCsv(file),
    onSuccess: (results) => {
      const validMappings = results.results
        .filter((r) => r.hubspot_valid && r.stripe_valid)
        .map((r) => ({
          hubspot_product_id: r.hubspot_product_id,
          stripe_meter_id: r.stripe_meter_id,
        }));
      setMeterDryRunResults(results);
      setPendingMeterMappings(validMappings);
      setMeterCsvDialogOpen(false);
      setMeterDryRunDialogOpen(true);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Meter CSV validation failed');
    },
  });

  const meterSaveMutation = useMutation({
    mutationFn: (mappings: MeterMappingRequest[]) =>
      apiClient.saveMeterMappings(mappings),
    onSuccess: (response) => {
      setMeterDryRunDialogOpen(false);
      setMeterDryRunResults(null);
      setPendingMeterMappings([]);
      queryClient.invalidateQueries({ queryKey: ['meterMappings'] });
      setError(summarizeSaveFailures(response.results, 'meter mapping'));
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Meter save failed');
    },
  });

  const meterDeleteMutation = useMutation({
    mutationFn: (hubspotProductId: string) =>
      apiClient.deleteMeterMapping(hubspotProductId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meterMappings'] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Meter delete failed');
    },
  });

  const handleLinkSubmit = (pairs: StagedPair[]) => {
    setError(null);
    validateMutation.mutate(
      pairs.map((p) => ({
        hubspot_product_id: p.hubspotId,
        stripe_price_id: p.stripeId,
        line_item_is_negative: p.isNegative ?? false,
      }))
    );
  };

  const handleCsvUpload = () => {
    setError(null);
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a CSV file');
      return;
    }
    csvValidateMutation.mutate(file);
  };

  const handleSaveValid = () => {
    if (!dryRunResults || pendingMappings.length === 0) return;
    const validIds = new Set(
      dryRunResults.results
        .filter((r) => r.hubspot_valid && r.stripe_valid)
        .map((r) => r.hubspot_product_id)
    );
    const toSave = pendingMappings.filter((m) =>
      validIds.has(m.hubspot_product_id)
    );
    saveMutation.mutate(toSave);
  };

  // ── Meter Mapping Handlers ───────────────────────────────────────────

  const handleMeterLinkSubmit = (pairs: StagedPair[]) => {
    setError(null);
    meterValidateMutation.mutate(
      pairs.map((p) => ({
        hubspot_product_id: p.hubspotId,
        stripe_meter_id: p.stripeId,
      }))
    );
  };

  const handleMeterCsvUpload = () => {
    setError(null);
    const file = meterFileInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a CSV file');
      return;
    }
    meterCsvValidateMutation.mutate(file);
  };

  const handleSaveMeterValid = () => {
    if (!meterDryRunResults || pendingMeterMappings.length === 0) return;
    const validIds = new Set(
      meterDryRunResults.results
        .filter((r) => r.hubspot_valid && r.stripe_valid)
        .map((r) => r.hubspot_product_id)
    );
    const toSave = pendingMeterMappings.filter((m) =>
      validIds.has(m.hubspot_product_id)
    );
    meterSaveMutation.mutate(toSave);
  };

  const productMappings = productData?.mappings ?? [];
  const overrideMappings = overrideData?.mappings ?? [];
  const meterMappings = meterData?.mappings ?? [];

  // IDs that already have a mapping, hidden from the linker's unlinked lists
  const mappedProductHubspotIds = new Set(productMappings.map((m) => m.hubspot_product_id));
  const mappedPriceIds = new Set(
    productMappings.map((m) => m.stripe_price_id).filter(Boolean) as string[]
  );
  const mappedMeterHubspotIds = new Set(meterMappings.map((m) => m.hubspot_product_id));
  const mappedMeterIds = new Set(
    meterMappings.map((m) => m.stripe_meter_id).filter(Boolean) as string[]
  );

  const renderExpandedRow = (
    key: string,
    colSpan: number,
    kind: MappingKind,
    hubspotId: string,
    stripeId?: string | null
  ) =>
    expandedRows.has(key) ? (
      <TableRow key={`${key}-detail`} className="hover:bg-transparent">
        <TableCell colSpan={colSpan} className="p-0">
          <MappingDetailPanel kind={kind} hubspotId={hubspotId} stripeId={stripeId} />
        </TableCell>
      </TableRow>
    ) : null;

  // A real button so keyboard users can reach the detail panel (row onClick
  // alone is pointer-only).
  const ExpandChevron = ({ rowKey, label }: { rowKey: string; label: string }) => {
    const expanded = expandedRows.has(rowKey);
    return (
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} details for ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleRow(rowKey);
        }}
        className="p-1 rounded hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
    );
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page Title */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Mappings
            </h1>
            <p className="text-muted-foreground">
              Map HubSpot products to Stripe prices and meters
            </p>
          </div>
          <BulkLinkButton />
        </div>

        {/* ── Section: Product Mappings ────────────────────────── */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            Product Mappings
          </h2>
          <p className="text-muted-foreground text-sm">
            HubSpot product to Stripe price mappings. Turn on{' '}
            <span className="font-medium text-foreground">Negative</span> for a
            product that should appear as a credit (a negative line) on the
            invoice — one-time products only.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="mb-6 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setError(null);
              setCsvDialogOpen(true);
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload CSV
          </Button>
          <Button
            onClick={() => {
              setError(null);
              setAddDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Mapping
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {negativeNotice && (
          <div className="mb-6">
            <Alert>
              <AlertDescription>{negativeNotice}</AlertDescription>
            </Alert>
          </div>
        )}

        {/* ── Table 1: Product Mappings ──────────────────────────── */}

        {productLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading product mappings...</p>
          </div>
        )}

        {productError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>Failed to load product mappings.</AlertDescription>
          </Alert>
        )}

        {!productLoading && !productError && (
          <div className="mb-10">
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>HubSpot Product ID</TableHead>
                    <TableHead>Stripe Price ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Negative</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productMappings.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center text-muted-foreground py-8"
                      >
                        No product mappings yet. Add a mapping or upload a CSV to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    productMappings.flatMap((mapping: ProductMappingItem) => {
                      const rowKey = `product:${mapping.id}`;
                      return [
                        <TableRow
                          key={mapping.id}
                          className="cursor-pointer"
                          onClick={() => toggleRow(rowKey)}
                        >
                          <TableCell>
                            <ExpandChevron rowKey={rowKey} label={mapping.hubspot_product_id} />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {portalId ? (
                              <ExternalLink
                                href={hubspotProductUrl(portalId, mapping.hubspot_product_id)}
                              >
                                {mapping.hubspot_product_id}
                              </ExternalLink>
                            ) : (
                              mapping.hubspot_product_id
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {mapping.stripe_price_id && stripeEnv ? (
                              <ExternalLink
                                href={stripePriceUrl(
                                  stripeEnv,
                                  mapping.stripe_price_id,
                                  stripeAcctId ?? undefined
                                )}
                              >
                                {mapping.stripe_price_id}
                              </ExternalLink>
                            ) : (
                              '\u2014'
                            )}
                          </TableCell>
                          <TableCell>
                            <SyncStatusBadge status={mapping.sync_status} />
                          </TableCell>
                          <TableCell>
                            {mapping.is_manual ? (
                              <Badge variant="outline">Manual</Badge>
                            ) : (
                              <Badge variant="secondary">Auto</Badge>
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={mapping.line_item_is_negative}
                                disabled={negativeMutation.isPending}
                                aria-label={`Bill ${mapping.hubspot_product_id} as a negative line item`}
                                onCheckedChange={(checked) => {
                                  setError(null);
                                  setNegativeNotice(null);
                                  negativeMutation.mutate({
                                    hubspotProductId: mapping.hubspot_product_id,
                                    isNegative: checked,
                                  });
                                }}
                              />
                              {mapping.line_item_is_negative && (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                  Credit
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {mapping.is_manual && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteMutation.mutate(mapping.hubspot_product_id);
                                }}
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>,
                        renderExpandedRow(
                          rowKey,
                          7,
                          'product',
                          mapping.hubspot_product_id,
                          mapping.stripe_price_id
                        ),
                      ];
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* ── Table 2: Line Item Overrides ────────────────────────── */}

        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            Line Item Overrides
          </h2>
          <p className="text-muted-foreground text-sm">
            Deal-specific price overrides where line item pricing differs from the product defaults
          </p>
        </div>

        {overrideLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading overrides...</p>
          </div>
        )}

        {overrideError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>Failed to load line item overrides.</AlertDescription>
          </Alert>
        )}

        {!overrideLoading && !overrideError && (
          <div className="mb-10">
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>HubSpot Line Item ID</TableHead>
                    <TableHead>Stripe Price ID</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrideMappings.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-muted-foreground py-8"
                      >
                        No line item overrides. Overrides are created automatically during sync when a line item's pricing differs from its product.
                      </TableCell>
                    </TableRow>
                  ) : (
                    overrideMappings.flatMap((override: LineItemOverrideItem) => {
                      const rowKey = `override:${override.id}`;
                      return [
                        <TableRow
                          key={override.id}
                          className="cursor-pointer"
                          onClick={() => toggleRow(rowKey)}
                        >
                          <TableCell>
                            <ExpandChevron rowKey={rowKey} label={override.hubspot_line_item_id} />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {override.hubspot_line_item_id}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {override.stripe_price_id && stripeEnv ? (
                              <ExternalLink
                                href={stripePriceUrl(
                                  stripeEnv,
                                  override.stripe_price_id,
                                  stripeAcctId ?? undefined
                                )}
                              >
                                {override.stripe_price_id}
                              </ExternalLink>
                            ) : (
                              '\u2014'
                            )}
                          </TableCell>
                          <TableCell>
                            <SyncStatusBadge status={override.sync_status} />
                          </TableCell>
                        </TableRow>,
                        renderExpandedRow(
                          rowKey,
                          4,
                          'override',
                          override.hubspot_line_item_id,
                          override.stripe_price_id
                        ),
                      ];
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* ── Section: Meter Mappings ──────────────────────────── */}

        <div className="mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            Meter Mappings
          </h2>
          <p className="text-muted-foreground text-sm">
            Map HubSpot metered products to Stripe billing meters
          </p>
        </div>

        <div className="mb-6 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setError(null);
              setMeterCsvDialogOpen(true);
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload CSV
          </Button>
          <Button
            onClick={() => {
              setError(null);
              setMeterAddDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Meter Mapping
          </Button>
        </div>

        {meterLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="mt-4 text-muted-foreground">Loading meter mappings...</p>
          </div>
        )}

        {meterError && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>Failed to load meter mappings.</AlertDescription>
          </Alert>
        )}

        {!meterLoading && !meterError && (
          <div>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>HubSpot Product ID</TableHead>
                    <TableHead>Stripe Meter ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {meterMappings.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center text-muted-foreground py-8"
                      >
                        No meter mappings yet. Add a mapping or upload a CSV to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    meterMappings.flatMap((mapping: MeterMappingItem) => {
                      const rowKey = `meter:${mapping.id}`;
                      return [
                        <TableRow
                          key={mapping.id}
                          className="cursor-pointer"
                          onClick={() => toggleRow(rowKey)}
                        >
                          <TableCell>
                            <ExpandChevron rowKey={rowKey} label={mapping.hubspot_product_id} />
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {portalId ? (
                              <ExternalLink
                                href={hubspotProductUrl(portalId, mapping.hubspot_product_id)}
                              >
                                {mapping.hubspot_product_id}
                              </ExternalLink>
                            ) : (
                              mapping.hubspot_product_id
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {mapping.stripe_meter_id && stripeEnv ? (
                              <ExternalLink
                                href={stripeMeterUrl(
                                  stripeEnv,
                                  mapping.stripe_meter_id,
                                  stripeAcctId ?? undefined
                                )}
                              >
                                {mapping.stripe_meter_id}
                              </ExternalLink>
                            ) : (
                              '\u2014'
                            )}
                          </TableCell>
                          <TableCell>
                            <SyncStatusBadge status={mapping.sync_status} />
                          </TableCell>
                          <TableCell>
                            {mapping.is_manual ? (
                              <Badge variant="outline">Manual</Badge>
                            ) : (
                              <Badge variant="secondary">Auto</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {mapping.is_manual && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  meterDeleteMutation.mutate(mapping.hubspot_product_id);
                                }}
                                disabled={meterDeleteMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>,
                        renderExpandedRow(
                          rowKey,
                          6,
                          'meter',
                          mapping.hubspot_product_id,
                          mapping.stripe_meter_id
                        ),
                      ];
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Add Mapping Modal (interactive linker) */}
      <LinkMappingModal
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        mode="price"
        mappedHubspotIds={mappedProductHubspotIds}
        mappedStripeIds={mappedPriceIds}
        onSubmit={handleLinkSubmit}
        submitting={validateMutation.isPending}
      />

      {/* CSV Upload Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with two columns: <code>hubspot_product_id</code> and{' '}
              <code>stripe_price_id</code>. An optional{' '}
              <code>line_item_is_negative</code> column (true/false) marks a
              product as a credit that appears as a negative line on the
              invoice. All rows will be validated before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                ref={fileInputRef}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCsvDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCsvUpload}
              disabled={csvValidateMutation.isPending}
            >
              {csvValidateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Upload & Validate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dry-Run Results Dialog */}
      <Dialog open={dryRunDialogOpen} onOpenChange={setDryRunDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Validation Results</DialogTitle>
            <DialogDescription>
              Review the validation results below. Only valid mappings will be saved.
            </DialogDescription>
          </DialogHeader>

          {dryRunResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">
                  Total: {dryRunResults.total_rows}
                </span>
                <span className="text-green-600">
                  Valid: {dryRunResults.valid_count}
                </span>
                <span className="text-red-600">
                  Invalid: {dryRunResults.invalid_count}
                </span>
                {dryRunResults.override_count > 0 && (
                  <span className="text-yellow-600">
                    Overrides: {dryRunResults.override_count}
                  </span>
                )}
              </div>

              {/* Results table */}
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>HubSpot Product ID</TableHead>
                      <TableHead>Stripe Price ID</TableHead>
                      <TableHead>HubSpot</TableHead>
                      <TableHead>Stripe</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dryRunResults.results.map(
                      (r: ProductMappingValidationResult, i: number) => (
                        <TableRow
                          key={i}
                          className={
                            !r.hubspot_valid || !r.stripe_valid
                              ? 'bg-red-50'
                              : ''
                          }
                        >
                          <TableCell className="font-mono text-xs">
                            {r.hubspot_product_id}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {r.stripe_price_id}
                          </TableCell>
                          <TableCell>
                            {r.hubspot_valid ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell>
                            {r.stripe_valid ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.hubspot_error || r.stripe_error || ''}
                            {r.is_override && (
                              <span className="text-yellow-600 ml-1">
                                (overrides {r.existing_stripe_id})
                              </span>
                            )}
                            {r.line_item_is_negative && (
                              <Badge className="ml-1 bg-amber-100 text-amber-800 hover:bg-amber-100">
                                Credit
                              </Badge>
                            )}
                            {r.negative_warning && (
                              <div className="text-yellow-600 mt-1">{r.negative_warning}</div>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDryRunDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveValid}
              disabled={
                saveMutation.isPending ||
                !dryRunResults ||
                dryRunResults.valid_count === 0
              }
            >
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save {dryRunResults?.valid_count ?? 0} Valid Mapping
              {(dryRunResults?.valid_count ?? 0) !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Meter Mapping Dialogs ─────────────────────────────── */}

      {/* Add Meter Mapping Modal (interactive linker) */}
      <LinkMappingModal
        open={meterAddDialogOpen}
        onOpenChange={setMeterAddDialogOpen}
        mode="meter"
        mappedHubspotIds={mappedMeterHubspotIds}
        mappedStripeIds={mappedMeterIds}
        onSubmit={handleMeterLinkSubmit}
        submitting={meterValidateMutation.isPending}
      />

      {/* Meter CSV Upload Dialog */}
      <Dialog open={meterCsvDialogOpen} onOpenChange={setMeterCsvDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Meter CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with two columns: <code>hubspot_product_id</code> and{' '}
              <code>stripe_meter_id</code>. All rows will be validated before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="meter-csv-file">CSV File</Label>
              <Input
                id="meter-csv-file"
                type="file"
                accept=".csv"
                ref={meterFileInputRef}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMeterCsvDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMeterCsvUpload}
              disabled={meterCsvValidateMutation.isPending}
            >
              {meterCsvValidateMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Upload & Validate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meter Dry-Run Results Dialog */}
      <Dialog open={meterDryRunDialogOpen} onOpenChange={setMeterDryRunDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Meter Validation Results</DialogTitle>
            <DialogDescription>
              Review the validation results below. Only valid mappings will be saved.
            </DialogDescription>
          </DialogHeader>

          {meterDryRunResults && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">
                  Total: {meterDryRunResults.total_rows}
                </span>
                <span className="text-green-600">
                  Valid: {meterDryRunResults.valid_count}
                </span>
                <span className="text-red-600">
                  Invalid: {meterDryRunResults.invalid_count}
                </span>
                {meterDryRunResults.override_count > 0 && (
                  <span className="text-yellow-600">
                    Overrides: {meterDryRunResults.override_count}
                  </span>
                )}
              </div>

              <div className="border rounded-lg max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>HubSpot Product ID</TableHead>
                      <TableHead>Stripe Meter ID</TableHead>
                      <TableHead>HubSpot</TableHead>
                      <TableHead>Stripe</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {meterDryRunResults.results.map(
                      (r: MeterMappingValidationResult, i: number) => (
                        <TableRow
                          key={i}
                          className={
                            !r.hubspot_valid || !r.stripe_valid
                              ? 'bg-red-50'
                              : ''
                          }
                        >
                          <TableCell className="font-mono text-xs">
                            {r.hubspot_product_id}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {r.stripe_meter_id}
                          </TableCell>
                          <TableCell>
                            {r.hubspot_valid ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell>
                            {r.stripe_valid ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {r.hubspot_error || r.stripe_error || ''}
                            {r.missing_properties && r.missing_properties.length > 0 && (
                              <span className="text-yellow-600 ml-1">
                                (missing: {r.missing_properties.join(', ')})
                              </span>
                            )}
                            {r.is_override && (
                              <span className="text-yellow-600 ml-1">
                                (overrides {r.existing_stripe_id})
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMeterDryRunDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveMeterValid}
              disabled={
                meterSaveMutation.isPending ||
                !meterDryRunResults ||
                meterDryRunResults.valid_count === 0
              }
            >
              {meterSaveMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Save {meterDryRunResults?.valid_count ?? 0} Valid Mapping
              {(meterDryRunResults?.valid_count ?? 0) !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Build an error summary for rows the save endpoint reported as failed
 *  (it returns HTTP 200 with per-row statuses). Null when nothing failed. */
function summarizeSaveFailures(
  results: Array<{ hubspot_product_id: string; status: string; error?: string }>,
  noun: string
): string | null {
  const failed = results.filter((r) => r.status === 'failed');
  if (failed.length === 0) return null;
  const details = failed
    .map((r) => `${r.hubspot_product_id}${r.error ? ` (${r.error})` : ''}`)
    .join(', ');
  return `${failed.length} of ${results.length} ${noun}${
    results.length !== 1 ? 's' : ''
  } failed to save: ${details}`;
}

function SyncStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'synced':
      return (
        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
          Synced
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
          Failed
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
          Pending
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default ProductMappings;
